<template>
  <ResponsiveLayout>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <CorporateHeader />
      <ProgressStepsContainer>
        <ProgressStepsItem
          state="done"
          muted
        >
          <BaseBanner
            variant="neutral"
            :title="$t('subscription_step_eligibility_title')"
            :description="$t('subscription_step_eligibility_description')"
          />
        </ProgressStepsItem>
        <ProgressStepsItem
          state="done"
          :muted="state !== STATES.CREATING_SUBSCRIPTION"
        >
          <BaseBanner
            variant="neutral"
            :title="$t('subscription_step_account_title')"
            :description="
              $t('subscription_step_account_description', {
                email: formData.email,
              })
            "
          />
        </ProgressStepsItem>
        <ProgressStepsItem
          v-if="[STATES.SUBSCRIPTION_CREATED, STATES.DOWNLOAD].includes(state)"
          state="done"
          :muted="state === STATES.DOWNLOAD"
        >
          <BaseBanner
            variant="neutral"
            :title="$t('subscription_step_subscription_title')"
            :description="$t('subscription_step_subscription_description')"
          />
        </ProgressStepsItem>
        <ProgressStepsItem
          v-if="
            [
              STATES.CREATING_SUBSCRIPTION,
              STATES.SUBSCRIPTION_CREATED,
            ].includes(state)
          "
          state="in_progress"
          muted
        >
          <BaseBanner
            variant="neutral"
            title="&nbsp;"
            description="&nbsp;"
          />
        </ProgressStepsItem>
        <GoToAppProgressStepItem v-if="state === STATES.DOWNLOAD" />
        <ProgressStepsItem
          v-if="state === STATES.SUBSCRIPTION_FAILED"
          state="todo"
        >
          <BaseBanner
            :description="errorBanner.description"
            :title="errorBanner.title"
            scroll-into-view
            variant="neutral"
          >
            <template #actions>
              <BaseButton
                v-if="errorBanner.ctaAction"
                :text="errorBanner.ctaText"
                class="cta"
                size="small"
                @click="errorBanner.ctaAction"
              />
              <GenericSupportModal
                v-if="errorBanner.showCustomerSupportButton"
                :additional-data="errorBanner.additionalData"
                class="cta"
                @click="redirectToRetry"
              >
                <template #trigger="{ open }">
                  <BaseButton
                    :variant="errorBanner.ctaAction ? 'outlined' : 'primary'"
                    class="cta"
                    size="small"
                    @click="open"
                  >
                    {{ $t('generic__contact_support') }}
                  </BaseButton>
                </template>
              </GenericSupportModal>
            </template>
          </BaseBanner>
        </ProgressStepsItem>
      </ProgressStepsContainer>
    </div>
    <template #footer><FooterLinks v-if="!isFromApp()" /></template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useUsStore from '@us/app/stores/useUsStore.js'
import { useI18n } from 'petite-vue-i18n'

import VERIFICATION_METHODS from '@us/app/config/verification-methods.js'
import ROUTE from '@us/app/router/appModule/names'

import BaseButton from '@shared/components/BaseButton.vue'
import CorporateHeader from '@shared/components/CorporateHeader.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import FooterLinks from '@us/app/components/FooterLinks.vue'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import BaseBanner from '@shared/components/BaseBanner.vue'
import ProgressStepsContainer from '@shared/components/ProgressStepsContainer.vue'
import ProgressStepsItem from '@shared/components/ProgressStepsItem.vue'
import { attempt, coerceAtLeast, wait } from '@shared/utils.js'
import GenericSupportModal from '@us/app/components/GenericSupportModal.vue'
import GoToAppProgressStepItem from '@us/app/components/GoToAppProgressStepItem.vue'
import HeaderHelp from '@us/app/components/HeaderHelp.vue'
import * as apiRequest from '@shared/api-endpoints/index.js'

const { t } = useI18n()
const userStore = useUserStore()
const corporateStore = useCorporateStore()
const usStore = useUsStore()
const router = useRouter()

const STATES = {
  CREATING_SUBSCRIPTION: 'creating_subscription',
  SUBSCRIPTION_CREATED: 'subscription_created',
  DOWNLOAD: 'download',
  SUBSCRIPTION_FAILED: 'subscription_failed',
}

const state = ref(STATES.CREATING_SUBSCRIPTION)

const errorBanner = ref(null)

const formData = computed(() => usStore.formData)
const corporate = computed(() => corporateStore.corporate)
const corporateEligibilityData = computed(
  () => usStore.corporateEligibilityData,
)

const redirectToRetry = async () => {
  await router.push({
    name: ROUTE.SELECT_VERIFICATION_METHOD,
    query: {
      retry: true,
    },
  })
}

const subscribe = async () => {
  errorBanner.value = null

  // when a user is already pro we will still try to create a new subscription.
  // For now this will do nothing on the backend, in the future this might update the subscription
  switch (corporateEligibilityData.value.type) {
    case VERIFICATION_METHODS.VOUCHER:
      await apiRequest.subscribeUserWithVoucher(
        corporateEligibilityData.value.voucher,
      )
      break
    case VERIFICATION_METHODS.ELIGIBLE_TOKEN:
      await apiRequest.subscribeWithEligibleToken(
        corporateEligibilityData.value,
      )
      break
    case VERIFICATION_METHODS.ELIGIBILITY_LIST:
      await apiRequest.subscribeWithVerificationData(
        corporateEligibilityData.value,
      )
      break
    default:
      console.warn(
        'UNSUPPORTED_SUBSCRIPTION_METHOD_AT_LOGIN',
        corporate.value.preferredSignupMethod,
        corporateEligibilityData.value.type,
      )
  }

  state.value = STATES.SUBSCRIPTION_CREATED
}

const handleError = (error) => {
  const errorKey = error?.response?.data?.error

  switch (true) {
    case errorKey === 'ALREADY_SUBSCRIBED': // uhchub + eligibility list
      state.value = STATES.SUBSCRIPTION_CREATED
      break
    case errorKey === 'CURRENT_SUBSCRIPTION_EXPIRATION_DATE_UNDEFINED': // voucher
      state.value = STATES.SUBSCRIPTION_CREATED
      break
    case errorKey === 'CORPORATE_DOES_NOT_ALLOW_RENEWAL': // voucher
      errorBanner.value = {
        title: t('generic__error_title'),
        description: t(
          'subscription_step_error_corporate_does_not_allow_renewal_description',
        ),
        showCustomerSupportButton: true,
        additionalData: { error },
      }
      state.value = STATES.SUBSCRIPTION_FAILED
      break
    case errorKey === 'ALREADY_USED_THIS_VOUCHER': // voucher
      errorBanner.value = {
        title: t('generic__error_title'),
        description: t(
          'subscription_step_error_voucher_already_used_description',
        ),
        ctaText: t('generic__try_again'),
        ctaAction: () => redirectToRetry(),
        showCustomerSupportButton: true,
      }
      state.value = STATES.SUBSCRIPTION_FAILED
      break
    case ['TOKEN_EXPIRED', 'TOKEN_INVALID'].includes(errorKey): // eligible token
      if (corporate.value.externalPortalUrl != null) {
        // specific message that redirects to portal
        errorBanner.value = {
          title: t('generic__error_title'),
          description: t(
            'b2b__external_portal_verification_error__description',
            { portal: corporate.value.externalPortalName },
          ),
          ctaText: t('b2b__external_portal_verification_error__action', {
            portal: corporate.value.externalPortalName,
          }),
          ctaAction: () =>
            window.location.assign(corporate.value.externalPortalUrl),
          showCustomerSupportButton: true,
        }
      } else {
        // generic message
        errorBanner.value = {
          title: t('generic__error_title'),
          description: t('subscription_step_error_token_expired_description'),
          showCustomerSupportButton: true,
        }
      }
      state.value = STATES.SUBSCRIPTION_FAILED
      break
    default:
      console.error(
        `creating subscription for user ${userStore.user?.id} failed with code ${error?.response?.status}`,
        error,
      )
      errorBanner.value = {
        title: t('generic__error_title'),
        description: t('subscription_step_error_generic_description'),
        showCustomerSupportButton: true,
        additionalData: { error },
      }
      state.value = STATES.SUBSCRIPTION_FAILED
  }
}

onMounted(async () => {
  // TODO do we need to handle page reloads?
  const startingTime = Date.now()

  const [, error] = await attempt(subscribe)

  // if subscription takes less than 1500ms, wait until 1500ms have passed
  const waitTime = coerceAtLeast(1500 - (Date.now() - startingTime), 0)
  await wait(waitTime)

  if (error) {
    handleError(error)
  }

  if (state.value === STATES.SUBSCRIPTION_CREATED) {
    await wait(1500)
    state.value = STATES.DOWNLOAD
  }
})
</script>

<style lang="scss" scoped>
.cta {
  width: fit-content;
}
</style>
